import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageCircle, X, Send, Bot, User, Loader2, Building2, Lightbulb, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AIChatbot = ({ user }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm PetBot, your AI assistant for International Pet Registry. I can help you:\n\n• Navigate the app\n• Connect your microchip registry as a partner\n• Submit feature requests\n• Answer questions about our services\n\nHow can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [partnerData, setPartnerData] = useState({
    registry_name: '',
    api_endpoint: '',
    contact_email: '',
    contact_phone: '',
    website_url: '',
    logo_url: '',
    description: ''
  });

  const [featureRequest, setFeatureRequest] = useState({
    title: '',
    description: '',
    email: ''
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickActions = [
    { label: 'Become a Partner', icon: Building2, action: () => setShowPartnerForm(true) },
    { label: 'Request Feature', icon: Lightbulb, action: () => setShowFeatureForm(true) },
    { label: 'View Partners', icon: Navigation, action: () => { navigate('/partners'); setIsOpen(false); } }
  ];

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post('/chatbot/message', {
        message: input,
        context: {
          user_logged_in: !!user,
          user_role: user?.role || 'guest',
          current_page: window.location.pathname
        }
      });

      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);

      // Check for navigation actions
      if (response.data.action) {
        handleAction(response.data.action);
      }
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble processing your request. You can try:\n\n• Clicking 'Become a Partner' below to connect your registry\n• Visiting our Partners page to see connected registries\n• Using the navigation menu to explore the app"
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = (action) => {
    switch (action.type) {
      case 'navigate':
        navigate(action.path);
        setIsOpen(false);
        break;
      case 'show_partner_form':
        setShowPartnerForm(true);
        break;
      case 'show_feature_form':
        setShowFeatureForm(true);
        break;
      default:
        break;
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post('/public/partner/register', partnerData);
      
      if (response.data.payment_url && response.data.payment_url.startsWith('https://checkout.stripe.com')) {
        toast.success('Redirecting to payment...');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Excellent! Your partner registration for "${partnerData.registry_name}" is ready!\n\nYour API Key: ${response.data.api_key}\n\n**Important:** Save this API key securely!\n\nOnce you complete the $20/month payment, your registry will be instantly connected to our federated search network.\n\nRedirecting you to payment now...`
        }]);
        setShowPartnerForm(false);
        setTimeout(() => {
          window.location.href = response.data.payment_url;
        }, 2000);
      } else {
        toast.success('Registration submitted!');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Your partner registration has been submitted! API Key: ${response.data.api_key}`
        }]);
        setShowPartnerForm(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeatureSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post('/feature-requests', {
        ...featureRequest,
        user_id: user?.user_id,
        user_name: user?.name,
        submitted_at: new Date().toISOString()
      });
      
      toast.success('Feature request submitted!');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Thank you for your feature request: "${featureRequest.title}"!\n\nOur admin team will review it and may reach out if they need more details. We appreciate your feedback in helping us improve International Pet Registry!`
      }]);
      setShowFeatureForm(false);
      setFeatureRequest({ title: '', description: '', email: '' });
    } catch (error) {
      toast.error('Failed to submit feature request');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-primary hover:bg-primary/90 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[450px] h-[600px] flex flex-col p-0 gap-0">
          <DialogHeader className="bg-primary text-white p-4 rounded-t-lg">
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-6 h-6" />
              PetBot Assistant
            </DialogTitle>
          </DialogHeader>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.role === 'user' 
                    ? 'bg-primary text-white rounded-br-md' 
                    : 'bg-white text-stone-800 rounded-bl-md shadow-sm'
                }`}>
                  <p className="text-sm whitespace-pre-line">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-stone-300 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-stone-600" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="px-4 py-2 bg-white border-t flex gap-2 overflow-x-auto">
            {quickActions.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={action.action}
                className="flex-shrink-0 text-xs"
              >
                <action.icon className="w-3 h-3 mr-1" />
                {action.label}
              </Button>
            ))}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
                className="flex-1"
                disabled={isLoading}
              />
              <Button onClick={sendMessage} disabled={isLoading || !input.trim()} className="bg-primary">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Registration Form Dialog */}
      <Dialog open={showPartnerForm} onOpenChange={setShowPartnerForm}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Connect Your Microchip Registry
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePartnerSubmit} className="space-y-4">
            <p className="text-sm text-stone-600">
              Join our federated microchip search network! For just <strong>$20/month</strong>, your registry will be instantly connected and searchable.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Registry/Company Name *</label>
                <Input
                  value={partnerData.registry_name}
                  onChange={(e) => setPartnerData({...partnerData, registry_name: e.target.value})}
                  placeholder="Your Company Name"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">API Endpoint *</label>
                <Input
                  value={partnerData.api_endpoint}
                  onChange={(e) => setPartnerData({...partnerData, api_endpoint: e.target.value})}
                  placeholder="https://api.yourregistry.com/lookup"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Contact Email *</label>
                <Input
                  type="email"
                  value={partnerData.contact_email}
                  onChange={(e) => setPartnerData({...partnerData, contact_email: e.target.value})}
                  placeholder="contact@company.com"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Phone Number *</label>
                <Input
                  value={partnerData.contact_phone}
                  onChange={(e) => setPartnerData({...partnerData, contact_phone: e.target.value})}
                  placeholder="+1-555-555-5555"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Website URL *</label>
                <Input
                  value={partnerData.website_url}
                  onChange={(e) => setPartnerData({...partnerData, website_url: e.target.value})}
                  placeholder="https://yourregistry.com"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Logo URL *</label>
                <Input
                  value={partnerData.logo_url}
                  onChange={(e) => setPartnerData({...partnerData, logo_url: e.target.value})}
                  placeholder="https://yourregistry.com/logo.png"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={partnerData.description}
                  onChange={(e) => setPartnerData({...partnerData, description: e.target.value})}
                  placeholder="Brief description of your registry"
                />
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-green-800">What happens next:</p>
              <ul className="text-green-700 mt-1 space-y-1">
                <li>• Complete $20/month payment via Stripe</li>
                <li>• Your registry is instantly connected</li>
                <li>• Receive your API credentials immediately</li>
                <li>• Your logo & contact info appears in search results</li>
              </ul>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowPartnerForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} className="bg-primary">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Continue to Payment ($20/month)
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feature Request Form Dialog */}
      <Dialog open={showFeatureForm} onOpenChange={setShowFeatureForm}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Request a Feature
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFeatureSubmit} className="space-y-4">
            <p className="text-sm text-stone-600">
              Have an idea to improve our platform? We'd love to hear it!
            </p>
            <div>
              <label className="text-sm font-medium">Feature Title *</label>
              <Input
                value={featureRequest.title}
                onChange={(e) => setFeatureRequest({...featureRequest, title: e.target.value})}
                placeholder="e.g., Export reports to PDF"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description *</label>
              <textarea
                value={featureRequest.description}
                onChange={(e) => setFeatureRequest({...featureRequest, description: e.target.value})}
                placeholder="Describe the feature and how it would help you..."
                className="w-full p-3 border rounded-md min-h-[100px] text-sm"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Your Email</label>
              <Input
                type="email"
                value={featureRequest.email}
                onChange={(e) => setFeatureRequest({...featureRequest, email: e.target.value})}
                placeholder="your@email.com (for updates)"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowFeatureForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading} className="bg-primary">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIChatbot;
